/**
 * StrategyEditDialog - View/Edit strategy details with component parameter editing
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Chip,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { getStrategyById, updateStrategy } from '../../api/algoBuilderXApi';
import { useSymbols } from '../../provider/symbolProvider';

const StrategyEditDialog = ({ open, onClose, strategyId, token, onSave }) => {
  const { symbolNames } = useSymbols();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  // Editable fields
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isEnabled: true,
    components: {
      indicators: [],
      entryRules: [],
      exitRules: [],
      lotSizer: null,
      riskManagers: []
    }
  });

  useEffect(() => {
    if (open && strategyId && token) {
      fetchStrategy();
    }
  }, [open, strategyId, token]);

  const fetchStrategy = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getStrategyById(token, strategyId);
      setStrategy(data);
      setFormData({
        name: data.name || '',
        description: data.description || '',
        isEnabled: data.isEnabled !== false,
        components: JSON.parse(JSON.stringify(data.components || {}))
      });
    } catch (err) {
      console.error('[StrategyEdit] Fetch failed:', err);
      setError('Failed to load strategy details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || formData.name.trim().length < 3) {
      toast.error('Strategy name must be at least 3 characters');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const updatePayload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        isEnabled: formData.isEnabled,
        components: formData.components
      };

      await updateStrategy(token, strategyId, updatePayload);
      toast.success('Strategy updated successfully');
      setEditMode(false);

      // Refresh strategy data
      await fetchStrategy();

      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('[StrategyEdit] Save failed:', err);
      setError(err.message || 'Failed to update strategy');
      toast.error(err.message || 'Failed to update strategy');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    setActiveTab(0);
    setError(null);
    onClose();
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'running': return '#22c55e';
      case 'stopped': return '#6b7280';
      case 'error': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // Check if a parameter key is likely a symbol field
  const isSymbolField = (key) => {
    const k = key.toLowerCase();
    return k.includes('symbol') || k === 'symbol1' || k === 'symbol2';
  };

  // Update component parameter value
  const updateComponentParam = (componentType, index, paramKey, newValue) => {
    setFormData(prev => {
      const updated = { ...prev };
      const components = { ...updated.components };

      if (componentType === 'lotSizer') {
        components.lotSizer = {
          ...components.lotSizer,
          parameters: {
            ...components.lotSizer?.parameters,
            [paramKey]: newValue
          }
        };
      } else {
        const arr = [...(components[componentType] || [])];
        if (arr[index]) {
          arr[index] = {
            ...arr[index],
            parameters: {
              ...arr[index].parameters,
              [paramKey]: newValue
            }
          };
        }
        components[componentType] = arr;
      }

      updated.components = components;
      return updated;
    });
  };

  // Get component data (from formData in edit mode, strategy in view mode)
  const getComponentData = (componentType) => {
    if (editMode) {
      return formData.components?.[componentType];
    }
    return strategy?.components?.[componentType];
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(255,255,255,0.05)',
      color: '#fff',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&.Mui-focused fieldset': { borderColor: '#2563eb' }
    },
    '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.5)' }
  };

  const renderComponentSection = (title, componentType, color) => {
    const components = getComponentData(componentType);

    if (!components || (Array.isArray(components) && components.length === 0)) {
      return null;
    }

    const items = Array.isArray(components) ? components : [components];
    const isSingle = componentType === 'lotSizer';

    return (
      <Box sx={{ mb: 3 }}>
        <Typography sx={{
          color: color,
          fontWeight: 600,
          fontSize: '0.875rem',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          {title}
          <Chip
            label={items.length}
            size="small"
            sx={{
              height: 18,
              fontSize: '0.7rem',
              backgroundColor: `${color}20`,
              color: color
            }}
          />
        </Typography>

        {items.map((comp, idx) => (
          <Box
            key={idx}
            sx={{
              backgroundColor: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '8px',
              p: 2,
              mb: 1
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.9rem', mb: 1.5 }}>
              {comp.componentId}
            </Typography>

            {comp.parameters && Object.keys(comp.parameters).length > 0 && (
              <Grid container spacing={2}>
                {Object.entries(comp.parameters).map(([key, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    {editMode ? (
                      // Editable field
                      isSymbolField(key) ? (
                        <FormControl fullWidth size="small">
                          <InputLabel sx={{ color: 'rgba(255,255,255,0.7)' }}>{key}</InputLabel>
                          <Select
                            value={value || ''}
                            label={key}
                            onChange={(e) => updateComponentParam(
                              componentType,
                              isSingle ? null : idx,
                              key,
                              e.target.value
                            )}
                            sx={textFieldSx}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: '#1e1e2e',
                                  border: '1px solid rgba(255,255,255,0.1)',
                                  maxHeight: 300,
                                  '& .MuiMenuItem-root': {
                                    color: '#fff',
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                                    '&.Mui-selected': { backgroundColor: `${color}30` }
                                  }
                                }
                              }
                            }}
                          >
                            {symbolNames.map(symbol => (
                              <MenuItem key={symbol} value={symbol}>{symbol}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : typeof value === 'boolean' ? (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={value}
                              onChange={(e) => updateComponentParam(
                                componentType,
                                isSingle ? null : idx,
                                key,
                                e.target.checked
                              )}
                              sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: color },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: color }
                              }}
                            />
                          }
                          label={<Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem' }}>{key}</Typography>}
                        />
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          label={key}
                          type={typeof value === 'number' ? 'number' : 'text'}
                          value={value}
                          onChange={(e) => updateComponentParam(
                            componentType,
                            isSingle ? null : idx,
                            key,
                            typeof value === 'number' ? parseFloat(e.target.value) || 0 : e.target.value
                          )}
                          sx={textFieldSx}
                        />
                      )
                    ) : (
                      // Read-only view
                      <Box sx={{
                        backgroundColor: 'rgba(255,255,255,0.05)',
                        borderRadius: '4px',
                        p: 1
                      }}>
                        <Typography sx={{
                          color: 'rgba(255,255,255,0.5)',
                          fontSize: '0.7rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px'
                        }}>
                          {key}
                        </Typography>
                        <Typography sx={{
                          color: '#fff',
                          fontSize: '0.85rem',
                          fontWeight: 500,
                          fontFamily: 'monospace'
                        }}>
                          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                        </Typography>
                      </Box>
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a2e',
          color: '#fff',
          borderRadius: '12px',
          minHeight: '500px',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          pb: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {editMode ? <EditIcon sx={{ color: '#2563eb' }} /> : <ViewIcon sx={{ color: '#27B56D' }} />}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {editMode ? 'Edit Strategy' : 'Strategy Details'}
            </Typography>
            {strategy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <Chip
                  label={strategy.status || 'Unknown'}
                  size="small"
                  sx={{
                    backgroundColor: `${getStatusColor(strategy.status)}20`,
                    color: getStatusColor(strategy.status),
                    fontWeight: 600,
                    fontSize: '0.7rem'
                  }}
                />
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                  ID: {strategyId}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            {error}
          </Alert>
        ) : strategy ? (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, v) => setActiveTab(v)}
              sx={{
                mb: 3,
                '& .MuiTab-root': { color: 'rgba(255,255,255,0.6)' },
                '& .Mui-selected': { color: '#2563eb' },
                '& .MuiTabs-indicator': { backgroundColor: '#2563eb' }
              }}
            >
              <Tab label="General" />
              <Tab label="Components" />
            </Tabs>

            {/* General Tab */}
            {activeTab === 0 && (
              <Box>
                {editMode ? (
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Strategy Name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        sx={textFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        sx={textFieldSx}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData.isEnabled}
                            onChange={(e) => setFormData(prev => ({ ...prev, isEnabled: e.target.checked }))}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#27B56D' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#27B56D' }
                            }}
                          />
                        }
                        label={<Typography sx={{ color: 'rgba(255,255,255,0.9)' }}>Enabled</Typography>}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Box>
                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', mb: 0.5 }}>
                        NAME
                      </Typography>
                      <Typography sx={{ color: '#fff', fontSize: '1.25rem', fontWeight: 600 }}>
                        {strategy.name}
                      </Typography>
                    </Box>

                    {strategy.description && (
                      <Box sx={{ mb: 3 }}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem', mb: 0.5 }}>
                          DESCRIPTION
                        </Typography>
                        <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem' }}>
                          {strategy.description}
                        </Typography>
                      </Box>
                    )}

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={3}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>STATUS</Typography>
                        <Chip
                          label={strategy.status || 'Unknown'}
                          size="small"
                          sx={{
                            mt: 0.5,
                            backgroundColor: `${getStatusColor(strategy.status)}20`,
                            color: getStatusColor(strategy.status),
                            fontWeight: 600
                          }}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>ENABLED</Typography>
                        <Typography sx={{ color: strategy.isEnabled ? '#22c55e' : '#ef4444', fontWeight: 600, mt: 0.5 }}>
                          {strategy.isEnabled ? 'Yes' : 'No'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>CREATED</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '0.85rem', mt: 0.5 }}>
                          {strategy.createdAt ? new Date(strategy.createdAt).toLocaleDateString() : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem' }}>UPDATED</Typography>
                        <Typography sx={{ color: '#fff', fontSize: '0.85rem', mt: 0.5 }}>
                          {strategy.updatedAt ? new Date(strategy.updatedAt).toLocaleDateString() : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {/* Components Tab */}
            {activeTab === 1 && (
              <Box>
                {renderComponentSection('Indicators', 'indicators', '#8b5cf6')}
                {renderComponentSection('Entry Rules', 'entryRules', '#22c55e')}
                {renderComponentSection('Exit Rules', 'exitRules', '#ef4444')}
                {renderComponentSection('Lot Sizer', 'lotSizer', '#2563eb')}
                {renderComponentSection('Risk Managers', 'riskManagers', '#f59e0b')}

                {!getComponentData('indicators')?.length &&
                 !getComponentData('entryRules')?.length &&
                 !getComponentData('exitRules')?.length &&
                 !getComponentData('lotSizer') &&
                 !getComponentData('riskManagers')?.length && (
                  <Typography sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', py: 4 }}>
                    No components configured
                  </Typography>
                )}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid rgba(255,255,255,0.1)',
          px: 3,
          py: 2,
          justifyContent: 'space-between'
        }}
      >
        <Button onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Close
        </Button>

        <Box sx={{ display: 'flex', gap: 1 }}>
          {!loading && strategy && (
            editMode ? (
              <>
                <Button
                  onClick={() => {
                    setEditMode(false);
                    setFormData({
                      name: strategy.name || '',
                      description: strategy.description || '',
                      isEnabled: strategy.isEnabled !== false,
                      components: JSON.parse(JSON.stringify(strategy.components || {}))
                    });
                  }}
                  sx={{ color: 'rgba(255,255,255,0.7)' }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                  sx={{
                    backgroundColor: '#2563eb',
                    '&:hover': { backgroundColor: '#1d4ed8' }
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={() => setEditMode(true)}
                startIcon={<EditIcon />}
                disabled={strategy.status?.toLowerCase() === 'running'}
                sx={{
                  backgroundColor: '#2563eb',
                  '&:hover': { backgroundColor: '#1d4ed8' },
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(37, 99, 235, 0.3)',
                    color: 'rgba(255,255,255,0.5)'
                  }
                }}
              >
                Edit
              </Button>
            )
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default StrategyEditDialog;
